/* eslint-disable tailwindcss/no-custom-classname */
import { ParsedUrlQuery } from "querystring";

import { NextPageWithLayout } from "next";
import { useRouter } from "next/router";
import {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";

import { UserPlan } from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
// import { pageApi } from "@/apis/page/pageApi";
// import serviceApi from "@/apis/service/serviceApi";
import { AdvertisementFooter } from "@/components/organisms/footer/AdvertisementFooter";
import { ServiceCalendarSelectDate } from "@/components/organisms/service-form/ServiceCalendarSelectDate";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { useLoading } from "@/states/loading/operations";
import {
  useFetchServiceCalendarForm,
  useFetchServiceFormByService,
  useHandleFieldValueChange,
} from "@/states/service-form/operations";
import {
  // useConfirmInputList,
  useServiceFormUiState,
} from "@/states/service-form/selectors";
// import { convertServiceMenuTimeRequired } from "@/utils/enum-converter";
import { Button } from "@/components/atoms/buttons/Button";
import StepHeaderLayout from "@/components/templates/layout/StepHeader";
import { ServiceFormServiceInputKey } from "@/states/service-form/types";
import { isIframePage } from "@/utils/common";

interface ItemProps {
  title: string;
  children: ReactNode;
  isWide?: boolean;
}
const ServiceDetailItem = ({ title, children, isWide = false }: ItemProps) => {
  return (
    <li
      className={`p-4 md:p-6 bg-white border-t border-t-theme-light first:border-t-0 grid gap-3 md:gap-4 ${isWide ? "grid-cols-1" : "grid-cols-1 md:grid-cols-[128px_auto]"
        }`}
    >
      <h4>
        <span className="block pl-2 font-bold text-primary bg-white border-l-2 border-primary">
          {title}
        </span>
      </h4>
      <div>{children}</div>
    </li>
  );
};

const CalendarPageSelect: NextPageWithLayout = () => {
  const router = useRouter();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const serviceFormUiState = useServiceFormUiState();
  const onFieldValueChange =
    useHandleFieldValueChange<ServiceFormServiceInputKey>();

  const fetchServiceCalendarForm = useFetchServiceCalendarForm();
  const fetchServiceFormByService = useFetchServiceFormByService();

  const queryParams:
    | {
      simplePageUuid: string;
      serviceMenuId: number;
      serviceMenuOptionIds: number[] | undefined;
    }
    | undefined = useMemo(() => {
      if (!router.isReady || !router.query) {
        return undefined;
      }
      const parseSimplePageUuid = (query: ParsedUrlQuery) => {
        const { uuid } = query;
        if (uuid === undefined || typeof uuid !== "string") {
          return undefined;
        }
        return uuid;
      };
      const parseServiceMenuId = (query: ParsedUrlQuery) => {
        const { service_menu_id: serviceMenuId } = query;
        if (serviceMenuId === undefined || typeof serviceMenuId !== "string") {
          return undefined;
        }
        if (Number.isInteger(Number(serviceMenuId))) {
          return Number(serviceMenuId);
        } else {
          return undefined;
        }
      };
      const parseServiceMenuOptionIds = (query: ParsedUrlQuery) => {
        const { options } = query;
        if (options === undefined || typeof options !== "string") {
          return undefined;
        }
        return options
          .replace(/^\[/, "")
          .replace(/]$/, "")
          .split(",")
          .filter((part) => Number.isInteger(Number(part)))
          .map((part) => Number(part));
      };
      const simplePageUuid = parseSimplePageUuid(router.query);
      const serviceMenuId = parseServiceMenuId(router.query);
      const serviceMenuOptionIds = parseServiceMenuOptionIds(router.query);
      if (simplePageUuid === undefined || serviceMenuId === undefined) {
        return undefined;
      }
      return {
        simplePageUuid,
        serviceMenuId,
        serviceMenuOptionIds,
      };
    }, [router.isReady, router.query]);

  const simplePageUuid = useMemo(() => {
    if (!queryParams || !queryParams.simplePageUuid) {
      return undefined;
    }
    return queryParams.simplePageUuid;
  }, [queryParams]);

  const formUuid = useMemo(() => {
    if (
      serviceFormUiState.status !== "Loaded" ||
      serviceFormUiState.formData.service.type !== "calendar"
    ) {
      return undefined;
    }
    return serviceFormUiState.formId;
  }, [serviceFormUiState]);

  const service = useMemo(() => {
    if (
      serviceFormUiState.status !== "Loaded" ||
      serviceFormUiState.formData.service.type !== "calendar"
    ) {
      return undefined;
    }
    return serviceFormUiState.formData.service;
  }, [serviceFormUiState]);

  useEffect(() => {
    void (async () => {
      if (!queryParams) {
        return;
      }

      await withLoading(async () => {
        try {
          const uuid = queryParams.simplePageUuid;
          if (uuid && isIframePage(uuid)) {
            await fetchServiceFormByService(+uuid);
          } else {
            await fetchServiceCalendarForm(uuid);
          }
        } catch (e) {
          if (e instanceof ApiResponseError) {
            await handlePageError(e);
          }
          throw e;
        }
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const handleSelectDate = useCallback(
    (date: Date) => {
      if (onFieldValueChange) {
        onFieldValueChange("serviceMenuReservationDateTime", date);
        onFieldValueChange("serviceMenuId", queryParams?.serviceMenuId);
        onFieldValueChange(
          "serviceMenuOptionIds",
          queryParams?.serviceMenuOptionIds ?? [],
        );
      }

      if (!formUuid) {
        return;
      }
      const formPath = `/form/${formUuid}`;
      void router.push(formPath);
    },
    [router, formUuid, simplePageUuid, onFieldValueChange],
  );

  const handleClickBackPage = () => {
    if (!formUuid) {
      return;
    }
    if (isIframePage(formUuid)) {
      void router.push(`/iframe/${formUuid}`);
    } else {
      void router.push(`/lp/${formUuid}`);
    }
  };
  return (
    <article className="flex flex-col items-center px-0 min-w-full">
      <div className="space-y-6 w-full max-w-[960px] break-all">
        <p className="text-[20px] text-center leading-[30px] font-bold mt-8">
          ご希望の日時を選択してください
        </p>
        <div>
          {queryParams?.serviceMenuId && (
            <ServiceCalendarSelectDate
              onSelectDate={handleSelectDate}
              serviceMenuId={queryParams?.serviceMenuId}
              serviceMenuOptionIds={queryParams?.serviceMenuOptionIds}
              defaultStartDate={new Date()}
            />
          )}
          <div className="px-4 md:px-0 md:w-[320px] mx-auto">
            <Button
              label="前の画面に戻る"
              onClick={handleClickBackPage}
              className="mt-10 md:mt-16  font-bold text-[#3ABCA8] block mx-auto w-full md:min-w-[320px] "
              size="xl"
              color="primary-outline"
            />
          </div>
        </div>
      </div>
      {serviceFormUiState.status === "Loaded" &&
        serviceFormUiState?.plan === UserPlan.FREE && (
          <>
            <AdvertisementFooter />
          </>
        )}
    </article>
  );
};

CalendarPageSelect.getLayout = function getLayout(children: ReactElement) {
  return (
    <StepHeaderLayout pageName="日程選択" classNames="bg-white">
      {children}
    </StepHeaderLayout>
  );
};

export default CalendarPageSelect;
